/**
 * Supplier Performance — Matrix view: a grade per month per supplier.
 *
 * Presentational: the page owns the data (`usePerformanceMatrix`) so its
 * "Export to Excel" can write the matrix on screen (Customer review 11 Sep,
 * review item 18).
 *
 * Customer review 11 Sep (item 2/3/4/17):
 *  - cells and legend show each band's configured label in its palette colour
 *    (the scale served with the rows — the same bands the Table grades with);
 *  - a row is the supplier over every customer site in view: the Customer
 *    column says which, there is no top-level Supplier Code, and a chevron
 *    opens one row per site;
 *  - a month cell is no longer a click-through to the supplier page.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { PerformanceMatrixState } from '../../pages/supplier-performance/matrix/usePerformanceMatrix';
import { MatrixSupplierRows } from '../../pages/supplier-performance/matrix/MatrixSupplierRows';

interface SupplierPerformanceMatrixViewProps {
  /** The grid, its scale and year controls — from `usePerformanceMatrix`. */
  matrix: PerformanceMatrixState;
}

export default function SupplierPerformanceMatrixView({ matrix }: SupplierPerformanceMatrixViewProps) {
  // PPM vs PPK — the backend blends with the platform's quality divisor, so
  // the tooltip label must follow the same toggle the Table uses.
  const qualityLabel = useQualityMeasureLabel();
  const {
    rows, total, bands, monthKeys, yearLabel, year, setYear, loading, error,
    requestParams, selectedBand, setSelectedBand, visibleRows, rowHoldsGrade, search,
  } = matrix;
  // CR-7297: the band legend or the page's quick search narrows the grid.
  const narrowed = !!selectedBand || !!search;
  const legend = [...bands].sort((a, b) => a.order - b.order);

  // Open per-site breakdowns; a new grid (year, filters, view) starts collapsed.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded(new Set());
  }, [rows]);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span>
            Performance Matrix ({narrowed ? `${visibleRows.length} of ${total}` : total} suppliers)
            {selectedBand && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                filtered to rating {legend.find((b) => b.grade === selectedBand)?.label ?? selectedBand}
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            {legend.length > 0 && (
              <div className="flex items-center gap-2 mr-4">
                {legend.map((t) => {
                  const count = rows.filter((r) => rowHoldsGrade(r, t.grade)).length;
                  const isSelected = selectedBand === t.grade;
                  const range = t.max_score !== undefined ? ` (${t.min_score}–${t.max_score})` : '';
                  return (
                    <button
                      key={`${t.order}-${t.grade}`}
                      type="button"
                      // These bands are a toggle, so announce the pressed state
                      // to assistive tech rather than relying on the colour ring.
                      aria-pressed={isSelected}
                      onClick={() => setSelectedBand(isSelected ? null : t.grade)}
                      disabled={count === 0 && !isSelected}
                      title={
                        count === 0
                          ? `No suppliers rated ${t.label}${range}`
                          : isSelected
                            ? 'Show all ratings'
                            : `Show only suppliers rated ${t.label}${range} (${count})`
                      }
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors disabled:opacity-40 disabled:cursor-default ${
                        isSelected ? 'bg-primary/10 ring-1 ring-primary' : 'hover:bg-muted'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: t.color }} />
                      <span className="text-xs text-muted-foreground">{t.label}</span>
                      {count > 0 && (
                        <span className="text-[10px] text-muted-foreground/70">({count})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold min-w-[160px] text-center">{yearLabel || year}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
                aria-label="Next year"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-red-600 text-center py-8">{error}</p>
        ) : visibleRows.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {rows.length > 0 && search
              ? `No supplier in ${yearLabel || year} matches “${search}”`
              : `No data for ${yearLabel || year}`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="border-b bg-muted/80 backdrop-blur-sm">
                  <th className="px-3 py-2.5 text-left font-semibold text-xs sticky left-0 bg-muted/80 backdrop-blur-sm z-10" style={{ minWidth: '240px' }}>
                    Supplier
                  </th>
                  <th
                    className="px-3 py-2.5 text-left font-semibold text-xs"
                    style={{ minWidth: '160px' }}
                    title="The customer sites this row covers — hover a cell for the list and each site’s supplier code"
                  >
                    Customer
                  </th>
                  {monthKeys.map((m) => (
                    <th key={m} className="px-2 py-2.5 text-center font-semibold text-xs" style={{ minWidth: '50px' }}>
                      {m}
                    </th>
                  ))}
                  <th className="px-2 py-2.5 text-center font-semibold text-xs border-l" style={{ minWidth: '80px' }}>
                    Overall Rating
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <MatrixSupplierRows
                    key={row.supplier_id}
                    row={row}
                    monthKeys={monthKeys}
                    bands={legend}
                    qualityLabel={qualityLabel}
                    year={year}
                    requestParams={requestParams}
                    expanded={expanded.has(row.supplier_id)}
                    onToggle={() => toggle(row.supplier_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
